import { css, CSSObject, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import {
    BreakpointKey,
    createResponsiveStyles,
    getSizeValue,
    mergeResponsiveStyleValues,
    ResponsiveStyleValue,
    useComponentDefaultProps,
    useHoneycombTheme,
} from '../../theming';
import { FixMeLater } from '../../utils';
import { Styler, StylerBaseProps } from '../Styler/Styler';

export function getContentMarginStyles(values: { contentMargin: number; disable: boolean }) {
    const { contentMargin, disable } = values;
    const padding = getSizeValue(disable ? 0 : contentMargin);
    return {
        paddingLeft: padding,
        paddingRight: padding,
    };
}

// Static styles
const STATIC_STYLES: SerializedStyles = css`
    position: relative;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    max-width: var(--max-width);
`;

const DEFAULT_MIN_WIDTH = 375;

export interface ContainerProps extends StylerBaseProps {
    /**
     * The content of the component.
     */
    children?: React.ReactNode;

    /**
     * If `true`, the default left and right padding will not be applied.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default false
     */
    disableContentMargins?: ResponsiveStyleValue<boolean>;

    /**
     * The maximum width of the container, equal to one of the theme breakpoints.
     * @default 2xl
     */
    maxWidth?: BreakpointKey;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: FixMeLater;
}

const defaultProps: Partial<ContainerProps> = {
    component: 'div',
    maxWidth: 'xxl',
    disableContentMargins: false,
};

export const Container: React.ForwardRefExoticComponent<ContainerProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Container(props: ContainerProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            disableContentMargins = false,
            maxWidth = 'xxl',
            ...other
        } = useComponentDefaultProps('Container', defaultProps, props);

        const { theme } = useHoneycombTheme();

        let maxWidthValue = theme.breakpoints.values[maxWidth];

        if (maxWidth === 'xs') {
            // 'xs' breakpoint should be zero
            maxWidthValue = Math.max(maxWidthValue, DEFAULT_MIN_WIDTH);
        }

        // Prepare CSS variables
        const cssVariables: React.CSSProperties = React.useMemo(() => {
            return {
                '--max-width': `${maxWidthValue}px`,
            } as React.CSSProperties;
        }, [maxWidthValue]);

        const dynamicStyles: CSSObject = React.useMemo(() => {
            return createResponsiveStyles(
                [
                    {
                        value: mergeResponsiveStyleValues(
                            { contentMargin: theme.breakpoints.contentMargins, disable: disableContentMargins },
                            theme
                        ),
                        getStyles: getContentMarginStyles,
                    },
                ],
                theme
            );
        }, [disableContentMargins, theme]);

        return (
            <Styler
                ref={ref}
                style={cssVariables}
                baseStyles={[STATIC_STYLES, dynamicStyles]}
                data-container={true}
                {...other}
            >
                {children}
            </Styler>
        );
    });
